import { ref, readonly } from "vue";
import { getCurrentDateAndSeason } from "~/utils/dateUtils";

/*
Improved chat flow with intelligent keyword generation:

1. User sends a message
2. LLM generates relevant search keywords from the user's message (in proper locale)
3. Search is performed using the generated keywords instead of the raw user message
4. Response is generated with product context from the search results

This approach provides better search results by:
- Converting natural language queries into specific product-related keywords
- Ensuring locale-appropriate keyword generation
- Taking into account current date and season for contextually relevant suggestions
- Maintaining fallback to original message if keyword generation fails
- Providing comprehensive logging for debugging and monitoring

The system relies on openai embeddings inside Meilisearch for search. If we want to use other embeddings later, 
we can use the embed.post.ts endpoint to generate the embedding and then use it in the search.post.ts endpoint.
*/

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  products?: any[];
}

export const useChat = () => {
  const messages = ref<ChatMessage[]>([]);
  const isLoading = ref(false);
  const isOpen = ref(false);

  const addMessage = (
    role: "user" | "assistant",
    content: string,
    products?: any[],
  ) => {
    const message: ChatMessage = {
      id: Date.now().toString(),
      role,
      content,
      timestamp: new Date(),
      products,
    };
    messages.value.push(message);
  };

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading.value) return;

    // Add user message
    addMessage("user", content);
    isLoading.value = true;

    try {
      // Get current locale
      const config = useRuntimeConfig();
      const locale = config.public.locale?.substring(0, 2) || "en";

      // Get current date and season information
      const { date: currentDate, season: currentSeason } =
        getCurrentDateAndSeason(locale);

      console.log("Chat: Starting message processing for locale:", locale);
      console.log("Chat: Current date and season:", currentDate, currentSeason);
      console.log("Chat: User message:", content);

      // Step 1: Generate search keywords using LLM
      const keywordPrompt = `You are a smart e-commerce search assistant. A user is asking: "${content}"

Current date: ${currentDate} (${currentSeason} season)

Generate 6-8 relevant search keywords that would help find the most appropriate products in our database. Focus on product names, categories, features, or use cases that would be most relevant.

IMPORTANT: 
- Generate the keywords in ${locale.toUpperCase()} language.
- Take into account the current season (${currentSeason}) when suggesting keywords.
- Avoid suggesting seasonal items that don't match the current season (e.g., don't suggest winter jackets in summer).
- Focus on products that are appropriate for the current season and date.

Format: comma-separated keywords.

Keywords:`;

      console.log(
        "Chat: Sending keyword generation prompt to LLM:",
        keywordPrompt,
      );

      let searchQuery = content; // Fallback to original message
      let keywords: string[] = [];

      try {
        const keywordResponse = await $fetch("/api/chat/generate-keywords", {
          method: "POST",
          body: {
            prompt: keywordPrompt,
            locale,
          },
        });

        // Parse keywords from LLM response
        const keywordsText = keywordResponse.keywords.trim();
        keywords = keywordsText
          .split(",")
          .map((keyword: string) => keyword.trim())
          .filter((keyword: string) => keyword.length > 0)
          .slice(0, 8);

        console.log("Chat: LLM generated keywords:", keywords);
        console.log("Chat: LLM response model:", keywordResponse.model);

        if (keywords.length > 0) {
          searchQuery = keywords.join(" ");
          console.log(
            "Chat: Using generated keywords for search:",
            searchQuery,
          );
        } else {
          console.log(
            "Chat: No valid keywords generated, falling back to original message",
          );
        }
      } catch (keywordError) {
        console.warn(
          "Chat: Keyword generation failed, falling back to original message:",
          keywordError,
        );
        searchQuery = content;
      }

      console.log("Chat: Final search query:", searchQuery);

      // Step 2: Search for relevant products using the generated keywords or fallback
      const searchResponse = await $fetch("/api/chat/search", {
        method: "POST",
        body: {
          query: searchQuery,
          limit: 1,
        },
      });

      console.log("Chat: Search completed with query:", searchQuery);
      console.log("Chat: Search results count:", searchResponse?.length || 0);

      if (searchResponse && searchResponse.length > 0) {
        console.log("Chat: First search result:", {
          name: searchResponse[0]?.name || "Unknown",
          sku: searchResponse[0]?.sku || "Unknown",
          category: searchResponse[0]?.category || "Unknown",
        });
      }

      // Step 3: Generate response with product context
      const chatMessages = messages.value.map((msg: ChatMessage) => ({
        role: msg.role,
        content: msg.content,
      }));

      console.log(
        "Chat: Generating response with",
        chatMessages.length,
        "chat messages and",
        searchResponse?.length || 0,
        "products",
      );

      const generateResponse = await $fetch("/api/chat/generate", {
        method: "POST",
        body: {
          messages: chatMessages,
          products: searchResponse,
        },
      });

      console.log("Chat: Response generation completed");
      console.log(
        "Chat: Generated response length:",
        generateResponse.content?.length || 0,
      );

      // Add assistant response
      addMessage("assistant", generateResponse.content, searchResponse);
    } catch (error: any) {
      console.error("Chat error:", {
        message: error?.message,
        status: error?.status,
        statusText: error?.statusText,
        data: error?.data,
        stack: error?.stack,
      });

      let errorMessage = "Sorry, I encountered an error. Please try again.";

      // Provide more specific error messages based on the error type
      if (error?.status === 500) {
        errorMessage =
          "Sorry, there was a server error. Please try again later.";
      } else if (error?.status === 429) {
        errorMessage =
          "Sorry, I'm receiving too many requests. Please wait a moment and try again.";
      } else if (error?.message?.includes("Failed to fetch")) {
        errorMessage =
          "Sorry, I couldn't connect to the server. Please check your internet connection.";
      }

      addMessage("assistant", errorMessage);
    } finally {
      isLoading.value = false;
    }
  };

  const clearChat = () => {
    messages.value = [];
  };

  const openChat = () => {
    isOpen.value = true;
  };

  const closeChat = () => {
    isOpen.value = false;
  };

  return {
    messages: readonly(messages),
    isLoading: readonly(isLoading),
    isOpen: readonly(isOpen),
    sendMessage,
    clearChat,
    openChat,
    closeChat,
  };
};
